import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Dropdown, Modal, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import StoryPreview from '../components/StoryPreview/StoryPreview';
import StoryViewer from '../components/StoryViewer/StoryViewer';
import StoryService from '../services/story';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './Stories.css';

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [updatingStoryId, setUpdatingStoryId] = useState(null);
  const [showStoryViewer, setShowStoryViewer] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState(null);
  
  // Schedule publish modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleStoryId, setScheduleStoryId] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [scheduleError, setScheduleError] = useState('');
  
  const { currentUser, logout, isAuthenticated } = useAuth();
  const { apps, currentApp, switchApp, loading: appsLoading } = useApp();
  const navigate = useNavigate();

  // Use refs to track the current app ID and prevent infinite loops
  const currentAppIdRef = useRef(null);
  const isFetchingRef = useRef(false);

  // Fetch stories function without dependencies that change frequently
  const fetchStories = useCallback(() => {
    // Don't fetch if already fetching, not authenticated, or no current app
    if (isFetchingRef.current || !isAuthenticated || !currentApp) return;
    
    // Don't fetch if the app ID hasn't changed
    if (currentAppIdRef.current === currentApp.id) return;
    
    isFetchingRef.current = true;
    setLoading(true);
    
    StoryService.getStories(null, currentApp.id)
      .then(response => {
        // Sort stories by orderIndex
        const sortedStories = [...(response.stories || [])].sort((a, b) => a.orderIndex - b.orderIndex);
        setStories(sortedStories);
        setError('');
        currentAppIdRef.current = currentApp.id;
      })
      .catch(err => {
        setError('Failed to load stories: ' + (err.response?.data || err.message));
      })
      .finally(() => {
        setLoading(false);
        isFetchingRef.current = false;
      });
  }, [isAuthenticated, currentApp]);

  // Effect to fetch stories when the app changes
  useEffect(() => {
    if (isAuthenticated && currentApp) {
      fetchStories();
    } else if (isAuthenticated && !currentApp) {
      // If authenticated but no current app, clear stories
      setStories([]);
    }
  }, [isAuthenticated, currentApp, fetchStories]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Reduce activation distance for more responsive dragging
      activationConstraint: {
        distance: 4,
        delay: 0,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    // If no over item or same item, do nothing
    if (!over || active.id === over.id) {
      return;
    }

    // Find the indices of the items
    const activeIndex = stories.findIndex(story => story.id.toString() === active.id);
    const overIndex = stories.findIndex(story => story.id.toString() === over.id);

    // Reorder the stories array
    const updatedStories = arrayMove(stories, activeIndex, overIndex);

    // Update the orderIndex values
    const storiesWithUpdatedOrder = updatedStories.map((story, index) => ({
      ...story,
      orderIndex: index
    }));

    // Update the state with animation
    setIsDragging(false);
    
    // Add a small delay to make the animation more noticeable
    setTimeout(() => {
      setStories(storiesWithUpdatedOrder);
    }, 50);

    // Update the order in the backend
    try {
      await StoryService.updateStoryOrder(
        storiesWithUpdatedOrder[overIndex].id,
        overIndex
      );
    } catch (err) {
      setError('Failed to update story order: ' + (err.response?.data || err.message));
      // Revert to the original order if there's an error
      fetchStories();
    }
  };

  // Handle drag start event
  const handleDragStart = () => {
    setIsDragging(true);
  };

  // This function is no longer used - we're removing the enable/disable functionality
  // Keeping the function signature for reference
  const handleToggleDisabled = async (storyId, currentDisabledState) => {
    // Function body removed as it's no longer used
  };

  // Sortable story item component
  const SortableStoryItem = ({ story, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging: isItemDragging,
    } = useSortable({ id: story.id.toString() });
    // Keep the card size consistent during dragging
    const style = {
      transform: CSS.Transform.toString(transform),
      transition: isItemDragging ? undefined : transition,
      opacity: isItemDragging ? 0.9 : 1,
      zIndex: isItemDragging ? 1000 : 1,
      width: '100%',
      height: '100%',
      boxSizing: 'border-box',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="story-item"
        data-dragging={isItemDragging}
        {...attributes}
      >
        {/* Apply drag handle only to the card header */}
        <Card
          className={`h-100 draggable-card ${story.disabled ? 'disabled-story' : ''}`}
          style={{
            boxShadow: isItemDragging ? '0 10px 20px rgba(0, 0, 0, 0.25)' : undefined,
            transform: isItemDragging ? 'scale(1)' : undefined,
            width: '100%',
            height: '100%',
            boxSizing: 'border-box'
          }}
        >
          {/* Separate drag handle that doesn't affect buttons */}
          <div className="drag-handle" {...listeners}>
            <div className="drag-handle-indicator" onClick={(e) => e.stopPropagation()}>
              <i className="bi bi-grip-horizontal"></i>
              <span>Drag to reorder</span>
            </div>
          </div>
          
          {story.disabled && (
            <div className="disabled-flag">DISABLED</div>
          )}
          {story.items.length === 0 && (
            <div className="empty-flag">NO CONTENT</div>
          )}
          {!story.published && story.items.length > 0 && (
            <div className="ready-flag">READY TO PUBLISH</div>
          )}
          {story.published && !story.publishStartTime && !story.publishEndTime && (
            <div className="published-flag">PUBLISHED</div>
          )}
          {story.published && story.publishStartTime && story.publishEndTime && (
            <>
              {isScheduleActive(story) ? (
                <div className="active-scheduled-flag">PUBLISHED According to Schedule</div>
              ) : isScheduleEnded(story) ? (
                <div className="ended-flag">ENDED</div>
              ) : (
                <div className="scheduled-flag">SCHEDULED</div>
              )}
            </>
          )}
          
          <div className="text-center pt-3 position-relative">
            <img
              src={story.circularImageUrl}
              alt={story.text || 'Story thumbnail'}
              className="rounded-circle"
              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
            />
            <div className="story-order-badge">
              {index + 1}
            </div>
          </div>
          <Card.Body>
            <Card.Title>{story.text || ''}</Card.Title>
            <Card.Text>
              Created: {formatDate(story.createdAt)}
              <br />
              Number of Content: {story.items.length}
              {story.publishStartTime && story.publishEndTime && (
                <div className="schedule-times">
                  <span>Start: {formatDateTime(story.publishStartTime + "Z")}</span>
                  <span>End: {formatDateTime(story.publishEndTime + "Z")}</span>
                </div>
              )}
            </Card.Text>
            <div className="w-100 mb-3">
              <Button
                variant="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewStory(story.id);
                }}
                disabled={isDragging}
                className="w-100 py-3 fs-5"
              >
                View Details
              </Button>
            </div>
            {/* Publish/Unpublish button */}
            <div className="w-100 mb-3">
              {story.published ? (
                <Button
                  variant="warning"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePublishStory(story.id, true);
                  }}
                  disabled={isDragging || updatingStoryId === story.id}
                  className="w-100 py-3 fs-5"
                >
                  {updatingStoryId === story.id ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Unpublish"
                  )}
                </Button>
              ) : (
                story.items.length > 0 && (
                  <>
                    <Button
                      variant="success"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePublishStory(story.id, false);
                      }}
                      disabled={isDragging || updatingStoryId === story.id}
                      className="w-100 py-3 fs-5 mb-2"
                    >
                      {updatingStoryId === story.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Publish"
                      )}
                    </Button>
                    <Button
                      variant="info"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenScheduleModal(story.id);
                      }}
                      disabled={isDragging || updatingStoryId === story.id}
                      className="w-100 py-3 fs-5"
                    >
                      Scheduled Publish
                    </Button>
                  </>
                )
              )}
            </div>
            <div className="w-100 mb-3">
              <Button
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteStory(story.id);
                }}
                disabled={isDragging}
                className="w-100 py-3 fs-5"
              >
                Delete
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  };

  const handleCreateStory = () => {
    navigate('/create-story');
  };

  const handleAppSwitch = (app) => {
    if (currentApp?.id !== app.id) {
      // Only switch if the app has changed
      switchApp(app);
      // Reset the current app ID ref to force a fetch
      currentAppIdRef.current = null;
    }
  };

  const handleViewStory = (storyId) => {
    navigate(`/story/${storyId}`);
  };
  
  const handleStoryPreviewClick = (storyId) => {
    setSelectedStoryId(storyId);
    setShowStoryViewer(true);
  };
  
  const handleCloseStoryViewer = () => {
    setShowStoryViewer(false);
    setSelectedStoryId(null);
  };

  const handlePublishStory = async (storyId, isCurrentlyPublished) => {
    const action = isCurrentlyPublished ? 'unpublish' : 'publish';
    const confirmMessage = `Are you sure you want to ${action} this story?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        setUpdatingStoryId(storyId);
        const response = await StoryService.toggleStoryPublished(storyId);
        
        if (response && response.success) {
          // Update local state
          setStories(prevStories =>
            prevStories.map(story => {
              if (story.id === storyId) {
                // If unpublishing, also clear the schedule times
                if (isCurrentlyPublished) {
                  return {
                    ...story,
                    published: false,
                    publishStartTime: null,
                    publishEndTime: null
                  };
                } else {
                  // Just publish without scheduling
                  return { ...story, published: true };
                }
              }
              return story;
            })
          );
        } else {
          // Handle server-side validation error
          setError(response.message || `Failed to ${action} story`);
        }
      } catch (err) {
        // Handle specific error message from the server
        const errorMessage = err.response?.data?.message || err.message;
        setError(`Failed to ${action} story: ` + errorMessage);
      } finally {
        setUpdatingStoryId(null);
      }
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (window.confirm('Are you sure you want to delete this story?')) {
      try {
        await StoryService.deleteStory(storyId);
        // Update the stories list directly instead of refetching
        setStories(prevStories => prevStories.filter(story => story.id !== storyId));
      } catch (err) {
        setError('Failed to delete story: ' + (err.response?.data || err.message));
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric',   hour: 'numeric', minute: 'numeric', second: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Parse the ISO string directly
      const date = new Date(dateString);
      
      // Format the date in UTC
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString; // Return the original string if there's an error
    }
  };
  
  // Check if a scheduled story is currently active (between start and end times)
  const isScheduleActive = (story) => {
    if (!story.publishStartTime || !story.publishEndTime) return false;
    
    try {
      // Get current time
      const now = new Date();
      // Parse the ISO strings directly - this will correctly handle the timezone
      const startTime = new Date(story.publishStartTime + "Z");
      const endTime = new Date(story.publishEndTime + "Z");
      
      // Debug logging
      console.log("Story ID:", story.id);
      console.log("Now:", now.toISOString());
      console.log("Start time:", startTime.toISOString());
      console.log("End time:", endTime.toISOString());
      console.log("Is active:", now >= startTime && now <= endTime);
      
      return now >= startTime && now <= endTime;
    } catch (error) {
      console.error("Error checking schedule active status:", error);
      return false;
    }
  };
  
  // Check if a scheduled story has ended
  const isScheduleEnded = (story) => {
    if (!story.publishEndTime) return false;
    
    try {
      // Get current time in UTC
      const now = new Date();
      // Parse the ISO string directly - this will correctly handle the timezone
      const endTime = new Date(story.publishEndTime + "Z");
      
      return now > endTime;
    } catch (error) {
      console.error("Error checking schedule ended status:", error);
      return false;
    }
  };
  
  const handleOpenScheduleModal = (storyId) => {
    setScheduleStoryId(storyId);
    
    // Set default dates (now for start, 24 hours later for end)
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Format dates for input fields
    setStartDate(formatDateForInput(now));
    setStartTime(formatTimeForInput(now));
    setEndDate(formatDateForInput(tomorrow));
    setEndTime(formatTimeForInput(tomorrow));
    
    setScheduleError('');
    setShowScheduleModal(true);
  };
  
  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  const formatTimeForInput = (date) => {
    return date.toTimeString().slice(0, 5);
  };
  
  const handleSchedulePublish = async () => {
    try {
      // Validate inputs
      if (!startDate || !startTime || !endDate || !endTime) {
        setScheduleError('All date and time fields are required');
        return;
      }
      
      // Create Date objects with proper timezone handling
      // This ensures the dates are interpreted in the user's local timezone
      const startDateTime = new Date(`${startDate}T${startTime}:00`);
      const endDateTime = new Date(`${endDate}T${endTime}:00`);
      
      // Validate date range
      if (startDateTime >= endDateTime) {
        setScheduleError('End time must be after start time');
        return;
      }
      
      setUpdatingStoryId(scheduleStoryId);
      
      // Call API with ISO strings (which include timezone information)
      const response = await StoryService.schedulePublish(
        scheduleStoryId,
        startDateTime.toISOString(),
        endDateTime.toISOString()
      );
      
      if (response && response.success) {
        // Update local state
        setStories(prevStories =>
          prevStories.map(story =>
            story.id === scheduleStoryId
              ? {
                  ...story,
                  published: true,
                  publishStartTime: startDateTime.toISOString(),
                  publishEndTime: endDateTime.toISOString()
                }
              : story
          )
        );
        
        // Close modal
        setShowScheduleModal(false);
      } else {
        // Handle server-side validation error
        setScheduleError(response.message || 'Failed to schedule publishing');
      }
    } catch (err) {
      // Handle specific error message from the server
      const errorMessage = err.response?.data?.message || err.message;
      setScheduleError('Failed to schedule publishing: ' + errorMessage);
    } finally {
      setUpdatingStoryId(null);
    }
  };

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center mb-3">
            <h2 className="me-3 mb-0">App: </h2>
            {isAuthenticated && !appsLoading && (
              <>
                {apps.length > 0 ? (
                  <Dropdown className="mb-3 mt-3">
                    <Dropdown.Toggle variant="primary" size='lg' id="app-dropdown">
                      {currentApp ? `${currentApp.name}` : 'Select App'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      {apps.map(app => (
                        <Dropdown.Item
                          key={app.id}
                          onClick={() => handleAppSwitch(app)}
                          active={currentApp && currentApp.id === app.id}
                          className="py-3 fs-5"
                        >
                          {app.name}
                        </Dropdown.Item>
                      ))}
                      <Dropdown.Divider />
                      <Dropdown.Item
                        as="button"
                        onClick={() => navigate('/apps')}
                        className="py-3 fs-5"
                      >
                        Manage Apps
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                ) : (
                  <Button
                    variant="primary"
                    className="mb-3 mt-3"
                    onClick={() => navigate('/apps')}
                  >
                    Create App
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="d-flex align-items-center">
            <h2>Preview     </h2>
          </div>
        </Col>
      </Row>
      
      {/* Story Preview Row */}
      {!loading && stories.length > 0 && (
        <StoryPreview
          stories={stories.filter(story => {
            // Only show published stories
            if (!story.published) return false;
            
            // For scheduled stories, check if they're currently active
            if (story.publishStartTime && story.publishEndTime) {
              const isActive = isScheduleActive(story);
              console.log("Story ID:", story.id, "Is active for preview:", isActive);
              
              return isActive;
            }
            
            // Show regular published stories
            return true;
          })}
          onStoryClick={handleStoryPreviewClick}
        />
      )}

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : !isAuthenticated ? (
        <Alert variant="info">
          Please log in to view your stories.
        </Alert>
      ) : apps.length === 0 ? (
        <Alert variant="info">
          You need to create an app before you can manage stories.
          <Button 
            variant="link" 
            onClick={() => navigate('/apps')}
            className="p-0 ms-2"
          >
            Create your first app
          </Button>
        </Alert>
      ) : !currentApp ? (
        <Alert variant="info">
          Please select an app to manage stories.
        </Alert>
      ) : stories.length === 0 ? (
        <div>
          <div className="d-flex align-items-center my-5">
            <h2>Get Started</h2>
            <Button
              variant="primary"
              size="lg"
              onClick={handleCreateStory}
              className="ms-3"
              disabled={!currentApp}
            >
              Create New Story <i className="bi bi-plus"></i>
            </Button>
          </div>
          <Alert variant="info">
            You don't have any stories in this app yet. Create your first story to get started.
          </Alert>
        </div>
      ) : (
        <div>
          <div className="d-flex align-items-center my-5">
            <h2>Edit</h2>
            <Button
              variant="primary"
              size="lg"
              onClick={handleCreateStory}
              className="ms-3"
              disabled={!currentApp}
            >
              Create New Story <i className="bi bi-plus"></i>
            </Button>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            measuring={{
              droppable: {
                strategy: 'always'
              }
            }}
          >
            <SortableContext
              items={stories.map(story => story.id.toString())}
              strategy={rectSortingStrategy}
            >
              <div className={`story-grid ${isDragging ? 'is-dragging' : ''}`}>
                {stories.map((story, index) => (
                  <SortableStoryItem
                    key={story.id.toString()}
                    story={story}
                    index={index}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
      
      {/* Schedule Publish Modal */}
      <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Schedule Publishing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {scheduleError && <Alert variant="danger">{scheduleError}</Alert>}
          
          <Alert variant="info">
            Enter times in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}).
            They will be converted to UTC when stored on the server and displayed in UTC format.
          </Alert>
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Start Time</Form.Label>
              <Form.Control
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>End Time</Form.Label>
              <Form.Control
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowScheduleModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSchedulePublish}
            disabled={updatingStoryId === scheduleStoryId}
          >
            {updatingStoryId === scheduleStoryId ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Schedule"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Story Viewer (Full Screen) */}
      {showStoryViewer && (
        <StoryViewer
          stories={stories}
          initialStoryId={selectedStoryId}
          onClose={handleCloseStoryViewer}
        />
      )}
    </Container>
  );
};

export default Stories;